<template>
  <div>
    <!-- Encabezado con título y botones de acción -->
    <Head :title="`${order.invoice_number ? 'Factura #' + order.invoice_number : 'Nota de Entrega #' + (order.number_order ?? order.id)} ${order.order_state === 'cancel' ? '| PEDIDO ANULADO' : ''}`" />

    <div class="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
      <div class="flex items-center">
        <button 
          class="mr-4 p-3 text-white bg-teal-800 hover:bg-teal-900 rounded transition-colors" 
          @click="back()"
          title="Volver atrás"
        >
          <i class="fa fa-arrow-left"></i>
        </button>
        
        <div>
          <h1 class="text-3xl font-bold flex items-center gap-3">
            <span v-if="order.invoice_number" class="text-blue-700">
              Factura #{{ order.invoice_number }}
            </span>
            <span v-else class="text-teal-800">
              Nota de Entrega #{{ order.number_order ?? order.id }}
            </span>

            <span 
              v-if="order.invoice_number"
              class="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800"
            >
              Factura
            </span>
            <span 
              v-else
              class="px-3 py-1 rounded-full text-xs font-semibold bg-teal-100 text-teal-800"
            >
              Nota de Entrega
            </span>

            <span v-if="order.order_state === 'cancel'" class="text-red-500 text-xl font-normal ml-2">
              PEDIDO ANULADO
            </span>
          </h1>
          <p v-if="order.invoice_number" class="text-sm text-gray-500 mt-0.5">
            Orden de origen: #{{ order.number_order ?? order.id }}
          </p>
        </div>
      </div>

      <!-- Botones de impresión y acciones agrupados -->
      <div class="flex flex-wrap items-center gap-2 justify-end">
        <!-- Backdrop transparente para cerrar dropdowns al hacer clic fuera -->
        <div 
          v-if="activeDropdown" 
          class="fixed inset-0 z-40 bg-transparent" 
          @click="closeDropdown"
        />

        <!-- GRUPO 1: Dropdown de Documentos PDF -->
        <div class="relative">
          <button 
            type="button"
            @click="toggleDropdown('docs')"
            :class="[
              'p-2.5 px-4 rounded-lg font-semibold text-sm flex items-center transition-all shadow-sm',
              activeDropdown === 'docs' 
                ? 'bg-blue-800 text-white ring-2 ring-blue-300' 
                : 'btn-blue text-white hover:opacity-95'
            ]"
          >
            <i class="fa fa-print mr-2" />
            <span>Documentos</span>
            <i :class="['fa ml-2 text-xs transition-transform', activeDropdown === 'docs' ? 'fa-chevron-up' : 'fa-chevron-down']" />
          </button>

          <!-- Menú desplegable de Documentos -->
          <div 
            v-if="activeDropdown === 'docs'" 
            class="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-2xl border border-gray-200 py-2 z-50 divide-y divide-gray-100"
          >
            <div class="px-4 py-2 text-xs font-bold text-gray-400 uppercase tracking-wider">
              Formatos PDF
            </div>

            <!-- Factura (si tiene correlativo asignado) -->
            <a
              v-if="order.invoice_number"
              target="_blank"
              :href="`/orders/${order.id}/report-invoice`"
              @click="closeDropdown"
              class="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
            >
              <div class="w-8 h-8 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center mr-3 flex-shrink-0">
                <i class="fa fa-file-invoice"></i>
              </div>
              <div>
                <div class="font-semibold text-gray-900">Factura</div>
                <div class="text-xs text-gray-500">Reporte oficial de factura</div>
              </div>
            </a>

            <!-- Nota de Entrega -->
            <a
              v-if="order.order_state !== 'estimate'"
              target="_blank"
              :href="`/orders/${order.id}/report-storer`"
              @click="closeDropdown"
              class="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-teal-50 hover:text-teal-700 transition-colors"
            >
              <div class="w-8 h-8 rounded-lg bg-teal-100 text-teal-700 flex items-center justify-center mr-3 flex-shrink-0">
                <i class="fa fa-file-lines"></i>
              </div>
              <div>
                <div class="font-semibold text-gray-900">Nota de Entrega</div>
                <div class="text-xs text-gray-500">Comprobante de almacén</div>
              </div>
            </a>

            <!-- Presupuesto -->
            <a
              target="_blank"
              :href="`/orders/${order.id}/report`"
              @click="closeDropdown"
              class="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-amber-50 hover:text-amber-700 transition-colors"
            >
              <div class="w-8 h-8 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center mr-3 flex-shrink-0">
                <i class="fa fa-file-contract"></i>
              </div>
              <div>
                <div class="font-semibold text-gray-900">Presupuesto</div>
                <div class="text-xs text-gray-500">Cotización formal</div>
              </div>
            </a>

            <!-- POS Digital -->
            <a
              v-if="order.order_state !== 'estimate'"
              target="_blank"
              :href="`/pos/${order.id}/show`"
              @click="closeDropdown"
              class="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-colors"
            >
              <div class="w-8 h-8 rounded-lg bg-purple-100 text-purple-700 flex items-center justify-center mr-3 flex-shrink-0">
                <i class="fa fa-cash-register"></i>
              </div>
              <div>
                <div class="font-semibold text-gray-900">Punto de Venta (POS)</div>
                <div class="text-xs text-gray-500">Vista de caja POS</div>
              </div>
            </a>
          </div>
        </div>

        <!-- GRUPO 2: Dropdown de Tickets Térmicos -->
        <div v-if="order.order_state !== 'estimate'" class="relative">
          <button 
            type="button"
            @click="toggleDropdown('tickets')"
            :class="[
              'p-2.5 px-4 rounded-lg font-semibold text-sm flex items-center transition-all shadow-sm',
              activeDropdown === 'tickets' 
                ? 'bg-blue-800 text-white ring-2 ring-blue-300' 
                : 'btn-blue text-white hover:opacity-95'
            ]"
          >
            <i class="fa fa-receipt mr-2" />
            <span>Tickets</span>
            <i :class="['fa ml-2 text-xs transition-transform', activeDropdown === 'tickets' ? 'fa-chevron-up' : 'fa-chevron-down']" />
          </button>

          <!-- Menú desplegable de Tickets -->
          <div 
            v-if="activeDropdown === 'tickets'" 
            class="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-2xl border border-gray-200 py-2 z-50 divide-y divide-gray-100"
          >
            <div class="px-4 py-2 text-xs font-bold text-gray-400 uppercase tracking-wider">
              Tickets Térmicos
            </div>

            <button 
              type="button"
              @click="printTicket('usd'); closeDropdown()"
              class="w-full flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors text-left"
            >
              <div class="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center mr-3 flex-shrink-0">
                <i class="fa fa-dollar-sign"></i>
              </div>
              <div>
                <div class="font-semibold text-gray-900">Ticket USD ($)</div>
                <div class="text-xs text-gray-500">Formato en Dólares</div>
              </div>
            </button>

            <button 
              type="button"
              @click="printTicket('ves'); closeDropdown()"
              class="w-full flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors text-left"
            >
              <div class="w-8 h-8 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center mr-3 flex-shrink-0">
                <i class="fa fa-money-bill-wave"></i>
              </div>
              <div>
                <div class="font-semibold text-gray-900">Ticket BsD (Bs)</div>
                <div class="text-xs text-gray-500">Formato en Bolívares</div>
              </div>
            </button>

            <button 
              v-if="$page.props.cop_change"
              type="button"
              @click="printTicket('cop'); closeDropdown()"
              class="w-full flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-amber-50 hover:text-amber-700 transition-colors text-left"
            >
              <div class="w-8 h-8 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center mr-3 flex-shrink-0">
                <i class="fa fa-coins"></i>
              </div>
              <div>
                <div class="font-semibold text-gray-900">Ticket COP ($)</div>
                <div class="text-xs text-gray-500">Pesos Colombianos</div>
              </div>
            </button>
          </div>
        </div>

        <!-- GRUPO 3: Convertir en factura / Editar correlativo -->
        <button
          v-if="order.order_state !== 'estimate' && order.order_state !== 'cancel'"
          @click="convertToInvoice"
          :class="[
            'p-2.5 px-4 rounded-lg font-semibold text-sm flex items-center transition-all shadow-sm text-white',
            order.invoice_number 
              ? 'bg-indigo-600 hover:bg-indigo-700' 
              : 'bg-emerald-600 hover:bg-emerald-700'
          ]"
        >
          <i class="fa fa-file-invoice mr-2" />
          <span v-if="!order.invoice_number">Convertir en factura</span>
          <span v-else>Editar correlativo</span>
        </button>

        <!-- GRUPO 4: Devolución -->
        <Link
          v-if="order.order_state !== 'estimate' && order.order_state !== 'cancel'"
          :href="`/devolutions/create?order_id=${order.id}`"
          class="p-2.5 px-4 rounded-lg text-white bg-red-600 hover:bg-red-700 transition-colors flex items-center font-semibold text-sm shadow-sm"
        >
          <i class="fa fa-undo mr-2" />
          Devolución
        </Link>
      </div>
    </div>

    <!-- Información del cliente y detalles de la orden -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6 p-6 bg-white rounded-lg shadow">
      <div class="space-y-3">
        <div class="grid grid-cols-2 gap-2">
          <div class="font-semibold">Cliente:</div>
          <div>{{ order.client.name }}</div>

          <div class="font-semibold">Alias/Cédula:</div>
          <div>{{ order.client.identification }}</div>

          <div class="font-semibold">Email:</div>
          <div>{{ order.client.email }}</div>

          <div class="font-semibold">Teléfono:</div>
          <div>{{ order.client.phone }}</div>
        </div>

        <!-- In the general information box, add discount info after the Total Bs line -->
        <!-- In the general information box, update discount info to use orderDiscount -->
        <div class="border-t pt-3 mt-3">
          <div class="grid grid-cols-2 gap-2">
            <div class="font-semibold">Balance USD:</div>
            <div>${{ order.balance.toFixed(2) }}</div>
        
            <div class="font-semibold">Balance Bs:</div>
            <div>{{ (order.balance * change).toFixed(2) }}</div>
        
            <div class="font-semibold">Deuda USD:</div>
            <div>${{ (order.total - order.balance).toFixed(2) }}</div>
        
            <div class="font-semibold">Deuda Bs:</div>
            <div>{{ ((order.total - order.balance) * change).toFixed(2) }}</div>
        
            <!-- Reordered to show Subtotal, Descuento, Total -->
            <div class="font-semibold">Subtotal USD:</div>
            <div>${{ Number(order.subtotal).toFixed(2) }}</div>
            
            <div v-if="order.discount && Number(order.discount) > 0" class="font-semibold text-red-600">Descuento USD:</div>
            <div v-if="order.discount && Number(order.discount) > 0" class="text-red-600">-${{ Number(order.discount).toFixed(2) }}</div>
            
            <div class="font-semibold">Total USD:</div>
            <div>${{ order.total.toFixed(2) }}</div>
        
            <div class="font-semibold">Total Bs:</div>
            <div>{{ (order.total * change).toFixed(2) }}</div>
          </div>
        </div>

        <div class="border-t pt-3 mt-3">
          <div class="grid grid-cols-2 gap-2">
            <div class="font-semibold">Fecha de venta:</div>
            <div>{{ order?.date }}</div>

            <div class="font-semibold">Tienda:</div>
            <div>{{ order?.organization?.name }}</div>

            <div class="font-semibold">Orden de compra:</div>
            <div>{{ order?.order_buy }}</div>

            <div class="font-semibold">Observaciones:</div>
            <div>{{ order?.comentary }}</div>

            <div class="font-semibold">Modalidad:</div>
            <div>{{ order.isCredit ? 'Crédito' : 'Contado' }}</div>

            <div class="font-semibold">Estado:</div>
            <div>
              <span :class="{
                'text-yellow-600': order.order_state === 'estimate',
                'text-red-600': order.order_state === 'cancel',
                'text-orange-600': order.order_state === 'request',
                'text-green-600': order.order_state === 'finish',
                'text-blue-600': order.order_state === 'pay',
                'text-purple-600': order.order_state === 'delivery'
              }">
                {{ getOrderStateLabel(order.order_state) }}
              </span>
            </div>
          </div>
        </div>

        <div v-if="order.order_state === 'estimate'" class="mt-4">
          <button 
            @click="changeOrderState()"
            class="btn-blue px-4 py-2 hover:opacity-90 transition-opacity"
          >
            Procesar orden
          </button>
        </div>
      </div>

      <div class="text-right">
        <div class="font-medium">
          Vendedor: {{ order?.responsible?.first_name }} {{ order?.responsible?.last_name }}
        </div>
      </div>
    </div>

    <!-- Agregar descuento -->
     

    <!-- Sección de pagos -->
    <div 
      v-if="canManagePayments"
      class="mb-6 p-6 bg-white rounded-lg shadow"
    >
      <h2 class="text-xl font-bold mb-6">Pagos</h2>

      <payment-box 
        v-if="order.order_state !== 'finish'"
        :quick="true"
        :payment_methods="payment_methods"
        :balance="order.total - order.balance"
        :change="change"
        :order="order"
      />

      <!-- Tabla de pagos -->
      <div class="mt-6">
        <table class="w-full">
          <thead>
            <tr>
              <th class="py-4 px-6 text-left">Método</th>
              <th class="py-4 px-6 text-left">Monto</th>
              <th class="py-4 px-6 text-left">Fecha</th>
              <th class="py-4 px-6 text-left">Referencia</th>
            </tr>
          </thead>
          <tbody>
            <tr 
              v-for="pay in order.pays" 
              :key="pay.id"
              class="border-t hover:bg-gray-50 transition-colors"
            >
              <td class="py-3 px-6 capitalize">{{ pay.method }}</td>
              <td class="py-3 px-6">
                {{ pay.amount }}
                <span v-if="pay.amount_bs > 0" class="text-gray-600">
                  (BsD. {{pay.amount_bs}})
                </span>
              </td>
              <td class="py-3 px-6">{{ formatDate(pay.created_at) }}</td>
              <td class="py-3 px-6">{{ pay.reference }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Sección de productos -->
    <div class="p-6 bg-white rounded-lg shadow">
      <h2 class="text-xl font-bold mb-6">Productos</h2>

      <div class="overflow-x-auto">
        <table class="w-full">
          <thead>
            <tr>
              <th class="py-4 px-6 text-left"></th>
              <th class="py-4 px-6 text-left">Producto</th>
              <th class="py-4 px-6 text-left">Descripción</th>
              <th class="py-4 px-6 text-left">Depósito</th>
              <th class="py-4 px-6 text-left">Estado</th>
              <th class="py-4 px-6 text-left">Cantidad</th>
              <th class="py-4 px-6 text-left">Precio</th>
              <th class="py-4 px-6 text-left">Total</th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="(product, key) in order.products"
              :key="product.id"
              :class="[
                'border-t',
                product.dispatch ? 'bg-blue-50' : '',
                'hover:bg-gray-50 transition-colors'
              ]"
            >
              <td class="py-3 px-6">
                <input
                  v-model="products.products[key]['dispatch']"
                  :value="product.id"
                  type="checkbox"
                  class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              </td>
              <td class="py-3 px-6">
                {{ product.product.name }}
                <div v-if="product.product.require_note" class="mt-2">
                  <div class="text-xs text-gray-500 font-medium mb-1">Nota requerida:</div>
                  <div class="flex items-center gap-2">
                    <input 
                      type="text" 
                      v-model="products.products[key].note" 
                      class="flex-1 px-2 py-1 border border-gray-300 rounded text-sm min-w-[150px]"
                    >
                    <button 
                      @click="updateNote(product.id, products.products[key].note)"
                      class="text-blue-600 hover:text-blue-800"
                      title="Guardar nota"
                    >
                      <i class="fa fa-save"></i>
                    </button>
                  </div>
                </div>
              </td>
              <td class="py-3 px-6">{{ product.product.description }}</td>
              <td class="py-3 px-6">{{ product.deposit.name }}</td>
              <td class="py-3 px-6">
                <span :class="product.dispatch ? 'text-green-600' : 'text-gray-600'">
                  {{ product.dispatch ? 'Despachado' : 'No Despachado' }}
                </span>
              </td>
              <td class="py-3 px-6">{{ product.quantity }}</td>
              <td class="py-3 px-6">
                ${{ product.price }}
                <div class="text-xs text-gray-500 font-medium">{{ getPriceGroup(product) }}</div>
              </td>
              <td class="py-3 px-6">${{ (product.price * product.quantity).toFixed(2) }}</td>
            </tr>
          </tbody>

          <tfoot>
            <tr class="bg-gray-50">
              <td colspan="6" class="py-3 px-6"></td>
              <td class="py-3 px-6 font-semibold">Subtotal</td>
              <td class="py-3 px-6">${{ Number(order.subtotal).toFixed(2) }}</td>
            </tr>
            <tr v-if="order.discount && Number(order.discount) > 0" class="bg-gray-50 text-red-600">
              <td colspan="6" class="py-3 px-6"></td>
              <td class="py-3 px-6 font-semibold">Descuento</td>
              <td class="py-3 px-6">-${{ Number(order.discount).toFixed(2) }}</td>
            </tr>
            <tr class="bg-gray-50 font-semibold">
              <td colspan="6" class="py-3 px-6"></td>
              <td class="py-3 px-6">Total</td>
              <td class="py-3 px-6">${{ Number(order.total).toFixed(2) }}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div class="mt-6">
        <button
          v-if="order.order_state === 'delivery'"
          @click="addDispatch"
          class="px-4 py-2 bg-teal-800 text-white rounded hover:bg-teal-900 transition-colors"
        >
          Despachar productos
        </button>
      </div>
    </div>

    <!-- Sección de despachos -->
    <div 
      v-if="showDispatchSection"
      class="mt-6 p-6 bg-white rounded-lg shadow"
    >
      <h2 class="text-xl font-bold mb-6">Despachos</h2>

      <table class="w-full">
        <thead>
          <tr>
            <th class="py-4 px-6 text-left">Concepto</th>
            <th class="py-4 px-6 text-left">Productos</th>
            <th class="py-4 px-6 text-left">Fecha</th>
            <th class="py-4 px-6 text-left">Responsable</th>
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="dispatch in dispatches"
            :key="dispatch.id"
            class="border-t hover:bg-gray-50 transition-colors"
          >
            <td class="py-3 px-6">Se realizó el despacho al cliente</td>
            <td class="py-3 px-6">
              {{ dispatch.products.map(p => {
                  let productImeis = order.imeis ? order.imeis.filter(i => i.product_id === p.product_id).map(i => i.imei) : [];
                  return p.product.name + (productImeis.length ? ` (IMEI: ${productImeis.join(', ')})` : '');
              }).join(' | ') }}
            </td>
            <td class="py-3 px-6">{{ formatDate(dispatch.date) }}</td>
            <td class="py-3 px-6">
              {{ getResponsibleName(dispatch.responsible) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Botón de anular orden -->
    <div 
      v-if="canCancelOrder"
      class="mt-6 p-6 bg-white rounded-lg shadow text-right"
    >
      <button
        @click="cancelOrder"
        class="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
      >
        Anular Orden
      </button>
    </div>

    <!-- Sección de descuento -->
    <div 
      v-if="canAddDiscount"
      class="mt-6 p-6 bg-white rounded-lg shadow"
    >
      <div class="flex justify-between items-center mb-4">
        <h2 class="text-xl font-bold">Descuento</h2>
        <button
          @click="showDiscountModal = true"
          class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
        >
          <i class="fa fa-percent mr-2"></i>
          Agregar Descuento
        </button>
      </div>

      <div v-if="order.discount && Number(order.discount) > 0" class="mt-4 p-4 bg-red-50 rounded-lg">
        <div class="flex justify-between items-center">
          <div>
            <span class="font-semibold text-red-600">Descuento actual:</span>
            <span class="text-red-600 ml-2">${{ Number(order.discount).toFixed(2) }}</span>
          </div>
          <button
            @click="removeDiscount"
            class="text-red-600 hover:text-red-700"
            title="Eliminar descuento"
          >
            <i class="fa fa-times"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Modal de descuento -->
    <div v-if="showDiscountModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 w-full max-w-md">
        <h3 class="text-xl font-bold mb-4">Agregar Descuento</h3>
        
        <div class="mb-4">
          <div class="flex items-center mb-4">
            <label class="inline-flex items-center mr-4">
              <input
                type="radio"
                v-model="discountForm.type"
                value="fixed"
                class="form-radio"
              >
              <span class="ml-2">Monto fijo</span>
            </label>
            <label class="inline-flex items-center">
              <input
                type="radio"
                v-model="discountForm.type"
                value="percentage"
                class="form-radio"
              >
              <span class="ml-2">Porcentaje</span>
            </label>
          </div>

          <label class="block text-sm font-medium text-gray-700 mb-2">
            {{ discountForm.type === 'fixed' ? 'Monto del descuento (USD)' : 'Porcentaje de descuento (%)' }}
          </label>
          <input
            v-model="discountForm.amount"
            type="number"
            step="0.01"
            min="0"
            :max="discountForm.type === 'fixed' ? order.total : 100"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            :placeholder="discountForm.type === 'fixed' ? 'Ingrese el monto del descuento' : 'Ingrese el porcentaje'"
          />
          
          <div v-if="discountForm.type === 'percentage'" class="mt-2 text-sm text-gray-600">
            Monto equivalente: ${{ calculatePercentageAmount }}
          </div>
        </div>

        <div class="flex justify-end gap-3">
          <button
            @click="showDiscountModal = false"
            class="px-4 py-2 text-gray-600 hover:text-gray-700"
          >
            Cancelar
          </button>
          <button
            @click="applyDiscount"
            class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            :disabled="!discountForm.amount || Number(discountForm.amount) <= 0"
          >
            Aplicar Descuento
          </button>
        </div>
      </div>
    </div>

    <!-- Modal IMEI para Despachos -->
    <div v-if="dispatchImeiModal.show" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 overflow-y-auto">
      <div class="bg-white rounded-lg shadow-xl w-full max-w-lg my-8">
        <div class="px-6 py-4 border-b flex justify-between items-center bg-gray-50">
          <h3 class="text-lg font-bold">Confirmar IMEIs para Despacho</h3>
          <button @click="closeDispatchImeiModal" class="text-gray-400 hover:text-gray-600">
            <i class="fa fa-times"></i>
          </button>
        </div>
        <div class="p-6 max-h-96 overflow-y-auto">
          <div v-for="product in dispatchImeiModal.products" :key="product.id" class="mb-6 pb-6 border-b last:border-0">
            <h4 class="font-bold text-gray-800 mb-2">{{ product.product.name }}</h4>
            <p class="text-sm text-gray-600 mb-4">Ingrese {{ product.quantity }} IMEI(s) correspondientes:</p>
            
            <div v-for="i in product.quantity" :key="i" class="mb-3">
              <text-input
                v-model="products.imeis[product.id][i-1]"
                :label="`IMEI ${i} *`"
                placeholder="Escanee o escriba el IMEI"
              />
            </div>
          </div>
        </div>
        <div class="px-6 py-4 bg-gray-50 border-t flex justify-end gap-2">
          <button @click="closeDispatchImeiModal" class="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
            Cancelar
          </button>
          <button 
            @click="saveDispatchImeis" 
            class="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors font-medium shadow-sm"
          >
            Confirmar y Despachar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput.vue'
import PaymentBox from '../../Shared/PaymentBox.vue'

export default {
  name: 'OrderShow',

  components: {
    Head,
    Link,
    TextInput,
    PaymentBox
  },

  layout: Layout,

  props: {
    order: {
      type: Object,
      required: true
    },
    groups: {
      type: Array,
      default: () => []
    },
    dispatches: {
      type: Object,
      required: true
    },
    payment_methods: {
      type: Object,
      required: true
    },
    change: {
      type: Number,
      required: true
    }
  },

  data() {
    return {
      activeDropdown: null,
      role: this.$parent.auth.user.role,
      form: this.$inertia.form({
        amount: 0,
        method: '',
        order_id: this.order.id,
        reference: '',
      }),
      products: this.$inertia.form({
        products: JSON.parse(JSON.stringify(this.order.products)),
        order_id: this.order.id,
        imeis: {}
      }),
      showInvoiceModal: false,
      invoiceCorrelative: this.order.number_order || '',
      showDiscountModal: false,
      dispatchImeiModal: {
        show: false,
        products: []
      },
      invoiceCorrelative: this.order.number_order || '',
      showDiscountModal: false,
      discountForm: {
        amount: '',
        type: 'fixed',
        order_id: this.order.id
      }
    }
  },

  computed: {
    canManagePayments() {
      return ['financial', 'admin', 'sales'].includes(this.role) && 
             this.order.order_state !== 'cancel' && 
             this.order.order_state !== 'estimate'
    },

    showDispatchSection() {
      return this.order.order_state !== 'cancel' && 
             this.order.order_state !== 'estimate'
    },

    canCancelOrder() {
      return this.order.pays.length === 0 && 
             this.order.order_state !== 'cancel'
    },

    canAddDiscount() {
      return ['admin', 'financial'].includes(this.role) && 
             this.order.order_state !== 'cancel' && 
             this.order.order_state !== 'estimate'
    },

    calculatePercentageAmount() {
      if (this.discountForm.type === 'percentage' && this.discountForm.amount) {
        return ((this.order.total * this.discountForm.amount) / 100).toFixed(2)
      }
      return '0.00'
    }
  },

  methods: {
    toggleDropdown(name) {
      this.activeDropdown = this.activeDropdown === name ? null : name
    },

    closeDropdown() {
      this.activeDropdown = null
    },

    back() {
      window.history.back()
    },

    convertToInvoice() {
      const correlative = prompt(
        'Ingrese el correlativo para la factura (dejar vacío para usar el mismo número de orden):',
        this.order.number_order || ''
      )
      
      if (correlative !== null) {
        this.$inertia.post(`/orders/${this.order.id}/invoice`, {
          invoice_number: correlative || this.order.number_order
        })
      }
    },

    cancelOrder() {
      if (confirm('¿Está seguro que desea anular esta orden?')) {
        window.location.href = `/orders/${this.order.id}/cancel`
      }
    },

    addDispatch() {
      const productsToDispatch = this.products.products.filter((p, index) => {
         return p.dispatch && !this.order.products[index].dispatch && p.product.has_imei;
      });

      if (productsToDispatch.length > 0) {
         this.dispatchImeiModal.products = productsToDispatch;
         
         productsToDispatch.forEach(p => {
             if (!this.products.imeis[p.id]) {
                 this.products.imeis[p.id] = Array(p.quantity).fill('');
             }
         });
         
         this.dispatchImeiModal.show = true;
         return; 
      }

      this.submitDispatch();
    },

    submitDispatch() {
      this.products.post('/orders/dispatch', {
        onSuccess: () => {
          this.dispatchImeiModal.show = false;
        }
      });
    },

    printTicket(exchange) {
      window.open(
        `/orders/${this.order.id}/report-thermical/${exchange}`,
        `Venta #${this.order.number_order || this.order.id}`,
        'width=400,height=500,toolbar=no'
      )
    },

    closeDispatchImeiModal() {
      this.dispatchImeiModal.show = false;
    },

    saveDispatchImeis() {
      // Validate all IMEIs are filled
      for (let p of this.dispatchImeiModal.products) {
        let imeis = this.products.imeis[p.id];
        let filledImeis = imeis.filter(i => i && i.trim() !== '');
        
        if (filledImeis.length !== p.quantity) {
          this.$swal?.fire({
            icon: 'error',
            title: 'Error',
            text: `Debe ingresar los ${p.quantity} IMEIs para el producto ${p.product.name}.`,
          }) || alert(`Debe ingresar los ${p.quantity} IMEIs para el producto ${p.product.name}.`);
          return;
        }
      }
      this.submitDispatch();
    },

    changeOrderState() {
      this.$inertia.get(`/orders/${this.order.id}/changeState`)
    },

    formatDate(date) {
      return new Date(date).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },

    getOrderStateLabel(state) {
      const labels = {
        estimate: 'PRESUPUESTO',
        cancel: 'ANULADA',
        request: 'PENDIENTE POR PAGO',
        finish: 'FINALIZADA',
        pay: 'PAGADA',
        delivery: 'EN ESPERA DE DESPACHO'
      }
      return labels[state] || state
    },

    getPriceGroup(orderProduct) {
      if (!orderProduct || !orderProduct.product) return '';
      
      const price = parseFloat(orderProduct.price);
      const product = orderProduct.product;
      
      if (price === parseFloat(product.cost)) return 'Costo';
      if (price === parseFloat(product.price)) return 'Precio Base';
      
      if (product.prices && this.groups) {
        for (const p of product.prices) {
          if (parseFloat(p.price) === price) {
            const group = this.groups.find(g => g.id === p.group_id);
            if (group) return group.name;
          }
        }
      }
      
      return 'Personalizado';
    },

    getResponsibleName(responsible) {
      if (!responsible) return ''
      return `${responsible.first_name} ${responsible.last_name}`
    },

    applyDiscount() {
      try {
        this.$inertia.post(`/orders/${this.order.id}/discount`, this.discountForm, {
          onSuccess: () => {
            this.showDiscountModal = false
            this.discountForm.amount = ''
          }
        })
      } catch (error) {
        console.error('Error applying discount:', error)
      }
    },

    async removeDiscount() {
      if (confirm('¿Está seguro que desea eliminar el descuento?')) {
        try {
          this.$inertia.delete(`/orders/${this.order.id}/discount`, {
            onSuccess: () => {
              // The page will be reloaded automatically by Inertia
            }
          })
        } catch (error) {
          console.error('Error removing discount:', error)
        }
      }
    },

    updateNote(orderProductId, note) {
      if (!note || note.trim() === '') {
        this.$swal?.fire({
          icon: 'error',
          title: 'Error',
          text: 'La nota no puede estar vacía'
        }) || alert('La nota no puede estar vacía');
        return;
      }
      
      this.$inertia.put(`/order-products/${orderProductId}/note`, { note }, {
        preserveScroll: true
      });
    }
  }
}
</script>
